import React, { useEffect, useState } from 'react';
import { Chip, Typography } from '@mui/material';
import { MixedListUiModuleBody } from '../../../../../API';
import { useLocales } from '../../../../../hooks';
import { toTitleCase } from '../../../../../utils/textUtils';
import { getContentTreatmentIcon } from '../../../../../utils/uiModuleUtils';
import { CollectionLink } from '../../../../Collections/CollectionLink';
import Localized from '../../../../shared/Localized';
import UIModuleListItem, { IBaseUIModuleListItemProps } from '../../UIModuleListItem';
import { getAssetUrl } from '../../../../../utils/assetHelpers';
import { useData } from '../../../../../data-layer';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { COLLECTION_ID_PARAM } from '../../../../../utils/consts/uiModules';
import { withPreviewCollectionId } from '../../../../../state/Layouts';
import CollectionLabel from '../../../../shared/CollectionLabel';
import { UIModuleListItemMarker } from '../../UIModuleListItemMarker';
import { MixedListPreview } from '../../ContentPreview/MixedListPreview';

export type ContentListListItemProps = IBaseUIModuleListItemProps<MixedListUiModuleBody>;

export const testIds = {
  treatment: 'content-list-list-item.treatment',
  collectionLink: 'content-list-list-item.collectionLink',
  preview: 'content-list-list-item.preview'
};

const defaultBackgroundPosition = 'center';

const MixedListListItem = ({ uiModule, ...props }: ContentListListItemProps): JSX.Element => {
  const { t } = useLocales();
  const {
    assets: {
      hook: { getByFilePath, getBackgroundPosition: getBgPosition },
      state: { withAssetByFilePath }
    },
    channels: {
      state: { withAllRecords: withAllChannels },
      hook: { getAll: getAllChannels }
    },
    collections: {
      state: { withMixedCollectionDisplay }
    }
  } = useData();
  const [backgroundPosition, setBackgroundPosition] = useState(defaultBackgroundPosition);
  const landscapeFillImageAsset = useRecoilValue(withAssetByFilePath(uiModule.landscapeFillImage));
  const previewCollectionId = useRecoilValue(withPreviewCollectionId);
  const allChannels = useRecoilValue(withAllChannels);
  const setMixedCollectionDisplay = useSetRecoilState(withMixedCollectionDisplay);

  const isDynamic = uiModule.contentListId === COLLECTION_ID_PARAM;
  const collectionId = isDynamic ? previewCollectionId : uiModule.contentListId;

  useEffect(() => {
    if (!allChannels) {
      getAllChannels();
    }

    setMixedCollectionDisplay(true);

    return () => {
      setMixedCollectionDisplay(false);
    };
  }, []);

  useEffect(() => {
    getBackgroundPosition();
  }, [uiModule, landscapeFillImageAsset]);

  const getBackgroundPosition = async () => {
    if (uiModule.landscapeFillImage) {
      const asset = landscapeFillImageAsset || (await getByFilePath(uiModule.landscapeFillImage));
      if (asset) {
        return setBackgroundPosition(getBgPosition(asset, defaultBackgroundPosition));
      }
    }
    setBackgroundPosition(defaultBackgroundPosition);
  };

  const getBackgroundImageUrl = () => {
    if (!uiModule.landscapeFillImage) return;
    return getAssetUrl(uiModule.landscapeFillImage);
  };

  return (
    <UIModuleListItem
      uiModule={uiModule}
      topLineTitle={
        uiModule.useCollectionTitle ? (
          <CollectionLabel collectionId={uiModule.contentListId} onlyName />
        ) : (
          <Localized prop={uiModule.title} />
        )
      }
      topLine={[
        <>
          <Typography variant="body2" color="textSecondary">
            {t('layouts.treatment')}:
          </Typography>
          <Chip
            icon={getContentTreatmentIcon(uiModule.contentTreatment)}
            label={toTitleCase(uiModule.contentTreatment)}
            size="small"
            data-testid={testIds.treatment}
          />
        </>
      ]}
      collectionLine={[
        <>
          <Typography variant="body2" color="textSecondary">
            {t('layouts.collection')}:
          </Typography>
          {!isDynamic && (
            <UIModuleListItemMarker>
              <CollectionLink data-testid={testIds.collectionLink} collectionId={uiModule.contentListId} />
            </UIModuleListItemMarker>
          )}
          {isDynamic && <Chip size="small" label={t('layouts.collection_id_param')} />}
        </>
      ]}
      renderPreview={(showPreview) => (
        <div data-testid={testIds.preview}>
          {collectionId && (
            <MixedListPreview
              collectionId={collectionId}
              treatment={uiModule.contentTreatment}
              showing={showPreview}
              backgroundImageUrl={getBackgroundImageUrl()}
              backgroundPosition={backgroundPosition}
            />
          )}
        </div>
      )}
      {...props}
    />
  );
};

export default MixedListListItem;
